import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { Screens } from '~/constants/config/permissions';
import { QcFullPage } from '~/features/content/components/qc/qc-full-page';
import { QcPageSkeleton } from '~/features/content/components/qc/qc-page-skeleton';
import { loadQcRunPage } from '~/features/content/components/qc/qc-preload';
import { ISSUE_SOURCES } from '~/features/content/utils/issue-source';
import {
  QC_PRODUCT_SORTS,
  QC_SAVED_VIEWS,
  QC_SCORES_VIEWS,
} from '~/features/content/utils/qc-products';
import { QC_RUN_STEPS } from '~/features/content/utils/qc-steps';
import { QUALITY_VIEWS } from '~/features/content/utils/quality-labels';
import { requirePermission } from '~/utils/guards';

// Shared with the Jobs tab's hover preload, so a preloaded chunk is the one this renders.
const QcRunPage = lazy(loadQcRunPage);

// The step lives in the URL so a reload, a shared link or the back button lands on it.
const searchSchema = z.object({
  step: z.enum(QC_RUN_STEPS).optional().catch(undefined),
  /** Summary: an earlier QC run to compare with. */
  compare: z.string().optional().catch(undefined),
  // Review products — the same keys as the job's review route, so a filtered view can be shared.
  product: z.string().optional().catch(undefined),
  // Review layout: Grid · Card · Content (the written content).
  view: z.enum(['grid', 'card', 'content']).optional().catch(undefined),
  q: z.string().max(100).optional().catch(undefined),
  type: z.string().optional().catch(undefined),
  qc: z.enum(['ANY', 'HIGH', 'MEDIUM', 'SUGGESTION', 'NONE']).optional().catch(undefined),
  rule: z.string().optional().catch(undefined),
  status: z.enum(['TODO', 'IN_PROGRESS', 'REVIEWED']).optional().catch(undefined),
  match: z.string().optional().catch(undefined),
  unsure: z.boolean().optional().catch(undefined),
  sort: z.enum(['FLAGS', 'LEAST_REVIEWED', 'PRODUCT']).optional().catch(undefined),
  batch: z.coerce.number().int().min(0).optional().catch(undefined),
  // Products & scores › Scores — channel, "View as" and batch, as on the job's Quality tab.
  qplatform: z.string().optional().catch(undefined),
  qview: z.enum(QUALITY_VIEWS).optional().catch(undefined),
  qbatch: z.coerce.number().int().min(0).optional().catch(undefined),
  // Products & scores — the sub-view, then the Products table's saved view, sort and filters.
  pview: z.enum(QC_SCORES_VIEWS).optional().catch(undefined),
  psv: z.enum(QC_SAVED_VIEWS).optional().catch(undefined),
  psort: z.enum(QC_PRODUCT_SORTS).optional().catch(undefined),
  ptype: z.string().max(100).optional().catch(undefined),
  pins: z.string().max(200).optional().catch(undefined),
  pcheck: z.string().max(200).optional().catch(undefined),
  pfield: z.string().max(100).optional().catch(undefined),
  psource: z.enum(ISSUE_SOURCES).optional().catch(undefined),
  pq: z.string().max(100).optional().catch(undefined),
  // Products & attributes › Products (a check that doesn't score) — search, filters, sort, paging.
  uq: z.string().max(100).optional().catch(undefined),
  utype: z.string().max(100).optional().catch(undefined),
  umatch: z.string().max(100).optional().catch(undefined),
  uqc: z.enum(['ANY', 'HIGH', 'MEDIUM', 'SUGGESTION', 'NONE']).optional().catch(undefined),
  urule: z.string().max(100).optional().catch(undefined),
  uunsure: z.boolean().optional().catch(undefined),
  ustatus: z.enum(['TODO', 'IN_PROGRESS', 'REVIEWED']).optional().catch(undefined),
  usort: z.enum(['FLAGS', 'LEAST_REVIEWED', 'PRODUCT']).optional().catch(undefined),
  ubatch: z.coerce.number().int().min(0).optional().catch(undefined),
  upage: z.coerce.number().int().min(1).optional().catch(undefined),
  usize: z.coerce
    .number()
    .pipe(z.union([z.literal(25), z.literal(50)]))
    .optional()
    .catch(undefined),
  uview: z.enum(['grid', 'table']).optional().catch(undefined),
  // Attribute worksheet — the picked product type and the attribute open in its drawer.
  wtype: z.string().max(100).optional().catch(undefined),
  wattr: z.string().max(200).optional().catch(undefined),
});

/** One QC run at `/content/qc/$runId` (a full page outside the Content tabs) — Run · Review · Products & scores · Summary. */
export const Route = createFileRoute('/_authenticated/_tenant/content_/qc/$runId')({
  // Outside the Content layout (its own full page), so it repeats the section's screen guard.
  beforeLoad: requirePermission(Screens.CONTENT_GENERATION),
  validateSearch: searchSchema,
  component: QcRunRoute,
});

function QcRunRoute() {
  const { runId } = Route.useParams();
  const { step, compare, ...rest } = Route.useSearch();
  return (
    <QcFullPage>
      <Suspense fallback={<QcPageSkeleton variant="run" step={step} runId={runId} />}>
        {/* Keyed by run: moving to another run (e.g. Compare's link) starts its state afresh. */}
        <QcRunPage key={runId} runId={runId} step={step} compare={compare} search={rest} />
      </Suspense>
    </QcFullPage>
  );
}
